export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, message: string, code = 'error') {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type Json = Record<string, unknown> | unknown[];

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    // The session ended somewhere else (signed out, removed from the project's guests, expired).
    if (res.status === 401 && !new URL(res.url, location.href).pathname.startsWith('/api/auth/')) window.dispatchEvent(new Event('mk:signed-out'));
    const body = data as { error?: string; code?: string } | null;
    const message = (body && typeof body === 'object' && body.error) || `Request failed (${res.status}).`;
    throw new ApiError(res.status, message, (body && typeof body === 'object' && body.code) || 'error');
  }
  return data as T;
}

async function request<T>(method: string, url: string, body?: Json | FormData): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.', 'network');
  }
  return parse<T>(res);
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body: Json | FormData = {}) => request<T>('POST', url, body),
  patch: <T>(url: string, body: Json) => request<T>('PATCH', url, body),
  put: <T>(url: string, body: Json) => request<T>('PUT', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

/** Multipart upload with progress (fetch cannot report upload progress). */
export function upload<T>(url: string, form: FormData, onProgress?: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'The upload was interrupted. Check your connection and try again.', 'network'));
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data as T);
      } else {
        const body = data as { error?: string; code?: string } | null;
        reject(new ApiError(xhr.status, body?.error || `Upload failed (${xhr.status}).`, body?.code));
      }
    };
    xhr.send(form);
  });
}

export const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
